/**
 * SIMS-9033 — what the administrator sees about a threshold change that is running, or that ran.
 *
 * The save returns before the cohort is written (AC1), so this is the only place the outcome is
 * ever reported. It has to survive navigation and time: AC3 says the result is still there "later
 * or on another day", which is why it is read from the Job rather than held in the page.
 */

export type PropagationJobStatus =
	| "IN_PROGRESS"
	| "COMPLETED"
	| "PARTIAL"
	| "FAILED";

export interface PropagationJob {
	/** Needed to retry this specific run (SIMS-9034). */
	_id?: string;
	status: PropagationJobStatus;
	startedAt: string;
	completedAt?: string;
	processed: number;
	skipped: number;
	failed: number;
	/**
	 * SIMS-9024 — why each skip happened (FR-26). These three SUM to `skipped`, which stays the
	 * total; the banner above still reads `skipped` and must keep doing so.
	 *
	 * OPTIONAL because a job recorded before SIMS-9024 has none of them. `undefined` is NOT zero:
	 * rendering "0 could not be reached" for such a job asserts something it never recorded. Show
	 * the combined figure instead.
	 */
	notReached?: number;
	alreadyAtTarget?: number;
	customised?: number;
	/** SIMS-9037 AC1 — the administrator who applied the change must be attributable (CR-1). */
	createdBy?: string | { _id?: string; firstName?: string; lastName?: string };
	thresholdPropagation?: {
		templateName: string;
		field: "TEMPLATE_DEFAULT" | "SEGMENT";
		segmentWeekRange?: { start: number; end: number };
		previousValue: number;
		nextValue: number;
		/** SIMS-9037 AC3 — a compliance artifact (D2), not a progress number. */
		acknowledgedDisenrollments?: number;
	};
}

/**
 * SIMS-9038 — what a change did to ONE participant.
 *
 * `NOT_IN_SCOPE` and `NO_PARTICIPANT_DATA` are deliberately different: the first means this
 * enrollment was not in the cohort, the second that the job predates per-participant records and
 * cannot say. Collapsing them would tell a support agent "not in scope" about a participant the
 * change may well have updated.
 */
export type ParticipantOutcomeKind =
	| "UPDATED"
	| "UNREACHABLE"
	| "ALREADY_AT_TARGET"
	| "CUSTOMISED"
	| "FAILED"
	| "NOT_IN_SCOPE"
	| "NO_PARTICIPANT_DATA";

/** One row of a change's cohort. IDS ONLY — a name here would be PHI (PO, 2026-09-22). */
export interface PropagationParticipant {
	enrollmentId: string;
	/** The participant id, resolved from the enrollment. Absent if it no longer resolves. */
	beneficiaryId?: string;
	outcome: ParticipantOutcomeKind;
	/** What THIS participant held — not the plan's value, which can differ. */
	previousValue?: number;
}

export interface PropagationParticipants {
	/**
	 * A job recorded before SIMS-9024 has no rows at all, which is NOT an empty cohort. Showing it
	 * as one would say "this change reached nobody" about a run that never recorded who it reached.
	 */
	predatesParticipantRecords: boolean;
	nextValue?: number;
	changedAt?: string;
	participants: PropagationParticipant[];
}

export type BannerSeverity = "info" | "success" | "warning" | "error";

export interface PropagationBanner {
	severity: BannerSeverity;
	title: string;
	detail: string;
	/** AC5 — a run that could not reach everyone must not read as fully applied. */
	isComplete: boolean;
	/** SIMS-9034 offers a retry only when something is actually retryable. */
	canRetry: boolean;
}

/** "segment 2 (weeks 7-12)" or "the plan default" — FR-8 wants the field named, not implied. */
const fieldLabel = (job: PropagationJob): string => {
	const detail = job.thresholdPropagation;
	if (!detail) {
		return "the disenrollment threshold";
	}
	if (detail.field === "SEGMENT" && detail.segmentWeekRange) {
		return `the segment covering weeks ${detail.segmentWeekRange.start}-${detail.segmentWeekRange.end}`;
	}
	return "the plan default";
};

const valueChange = (job: PropagationJob): string => {
	const detail = job.thresholdPropagation;
	return detail
		? ` from ${detail.previousValue} to ${detail.nextValue} days`
		: "";
};

const participants = (n: number): string =>
	`${n} participant${n === 1 ? "" : "s"}`;

/**
 * The banner for the most recent propagation on a plan, or null when there has never been one.
 *
 * Deliberately says what happened to EVERY participant, not just the successes: AC3 asks for
 * updated, left alone, and could-not-be-reached, because a number that only counts successes
 * cannot be reconciled against the cohort the administrator was shown at confirmation time.
 */
export const propagationBanner = (
	job: PropagationJob | null | undefined,
): PropagationBanner | null => {
	if (!job) {
		return null;
	}

	const what = `${fieldLabel(job)}${valueChange(job)}`;

	// AC2 — in progress, and how far it has got. The counts are live: the job records them as it
	// works, so this is real progress rather than a spinner.
	if (job.status === "IN_PROGRESS") {
		return {
			severity: "info",
			title: "Applying this change to current participants…",
			detail: `Changing ${what}. ${participants(job.processed)} updated so far.`,
			isComplete: false,
			canRetry: false,
		};
	}

	if (job.status === "FAILED") {
		return {
			severity: "error",
			title: "This change could not be applied",
			detail: `Changing ${what} did not complete. No participant was left partly updated — each is either changed or untouched.`,
			isComplete: false,
			canRetry: true,
		};
	}

	// AC5 — some could not be reached, so this must NOT read as fully applied.
	if (job.status === "PARTIAL") {
		return {
			severity: "warning",
			title: "This change reached some participants, but not all",
			detail:
				`Changed ${what}. ${participants(job.processed)} updated, ` +
				`${job.skipped} left as they were, ` +
				`${job.failed} could not be reached.`,
			isComplete: false,
			canRetry: true,
		};
	}

	return {
		severity: "success",
		title: "This change has been applied to current participants",
		detail:
			`Changed ${what}. ${participants(job.processed)} updated, ` +
			`${job.skipped} left as they were.`,
		isComplete: true,
		canRetry: false,
	};
};

/**
 * Should the page keep asking? Only while work is actually outstanding.
 *
 * Polling a finished job forever would be a background request per plan view for no benefit, and
 * AC3's durable result does not need refreshing — it cannot change.
 */
export const shouldPollPropagation = (
	job: PropagationJob | null | undefined,
): boolean => job?.status === "IN_PROGRESS";

// ---------------------------------------------------------------------------------------------
// SIMS-9024 — the change history (SIMS-9037) and the per-participant lookup (SIMS-9038).
//
// Pure functions, deliberately: the copy is the specification here. Decision D15 means there is
// no design for this epic, so the acceptance criteria ARE the contract, and keeping the wording
// testable in isolation is what stops it drifting from them.
// ---------------------------------------------------------------------------------------------

/** What moved, for a history row. Reuses the banner's vocabulary so one screen speaks one dialect. */
export const historyWhatChanged = (job: PropagationJob): string =>
	`${fieldLabel(job)}${valueChange(job)}`;

/**
 * The participant counts for one history entry.
 *
 * SIMS-9037 AC6 needs "could not be reached" reported SEPARATELY, which is only possible when the
 * job carries the FR-26 breakdown. A job recorded before SIMS-9024 has `notReached === undefined`,
 * and that is NOT zero: claiming "0 could not be reached" about a run that never recorded the
 * distinction is a false statement, so those jobs show the combined figure instead.
 */
export const historyCounts = (
	job: PropagationJob,
): { updated: string; leftAlone: string; notReached?: string } => {
	const hasBreakdown = typeof job.notReached === "number";

	if (!hasBreakdown) {
		return {
			updated: `${participants(job.processed)} updated`,
			leftAlone: `${job.skipped} left as they were or could not be reached`,
		};
	}

	const leftAlone = (job.alreadyAtTarget ?? 0) + (job.customised ?? 0);

	return {
		updated: `${participants(job.processed)} updated`,
		leftAlone: `${leftAlone} left as they were`,
		notReached: `${job.notReached} could not be reached`,
	};
};

/**
 * Did this run reach everyone? Drives whether the entry reads as a plain success.
 *
 * AC6: a run that did not reach every participant must NOT read as fully applied. `notReached` is
 * the only signal for that — `skipped` also counts participants who were already correct or
 * deliberately preserved, neither of which is a failure to reach anyone.
 */
export const historyReachedEveryone = (job: PropagationJob): boolean =>
	job.status !== "PARTIAL" &&
	job.status !== "FAILED" &&
	(job.notReached ?? 0) === 0;

/**
 * SIMS-9037 AC3 — the figure the administrator typed back before applying.
 *
 * Shown even when zero: its absence would be indistinguishable from "this run predates the
 * acknowledgment", and per D2 this is the durable evidence of a deliberate participant impact.
 */
export const historyAcknowledgment = (job: PropagationJob): string => {
	const n = job.thresholdPropagation?.acknowledgedDisenrollments;
	if (n === undefined) {
		return "";
	}
	return n === 0
		? "No forecast disenrollments"
		: `${n} forecast disenrollment${n === 1 ? "" : "s"} acknowledged`;
};

/**
 * SIMS-9038 — the answer for one participant, in words a support agent can use on a ticket.
 *
 * Every outcome gets its OWN sentence. CUSTOMISED and ALREADY_AT_TARGET both mean "left as they
 * were", but only the first is the case AC2 describes, and giving the wrong one is worse than
 * giving none. UNREACHABLE and FAILED both mean "did not apply", but only FAILED can be retried.
 */
/**
 * SIMS-9038 — the outcome for ONE participant, as a table row reads it.
 *
 * Every outcome gets its own words. CUSTOMISED and ALREADY_AT_TARGET both mean "left as they
 * were", but only the first is the case AC2 describes, and giving a support agent the wrong one is
 * worse than giving none. UNREACHABLE and FAILED both mean "did not apply", but only FAILED can be
 * retried.
 *
 * `label` is the cell; `why` qualifies it with what the participant actually held, which is what
 * AC2's "and why" asks for.
 */
export const participantOutcomeLabel = (
	participant: PropagationParticipant,
	nextValue?: number,
): { label: string; why: string } => {
	const days = (n?: number): string =>
		n === undefined ? "\u2014" : `${n} days`;

	switch (participant.outcome) {
		case "UPDATED":
			return {
				label: "Updated",
				why: `From ${days(participant.previousValue)} to ${days(nextValue)}.`,
			};
		case "CUSTOMISED":
			return {
				label: "Left as they were",
				why: `Holds ${days(participant.previousValue)}, set specifically for them.`,
			};
		case "ALREADY_AT_TARGET":
			return {
				label: "Left as they were",
				why: `Already on ${days(participant.previousValue)}.`,
			};
		case "UNREACHABLE":
			return {
				label: "Could not be reached",
				why: "Their enrollment has no phase matching this change.",
			};
		case "FAILED":
			return {
				label: "Could not be applied",
				why: "It was attempted and failed. This participant can be retried.",
			};
		default:
			return { label: String(participant.outcome), why: "" };
	}
};
